<template>
  <div>
    <div class="pb-5">
      <ul class="space-y-1 select-none">
        <li
          v-for="course_setting in courseSettingsWithDiscount"
          class="ln-radiobutton"
          :key="course_setting.id"
        >
          <label class="block relative group">
            <input
              type="radio"
              name="course-setting-option"
              :value="course_setting.id"
              v-model="selectedCourseSettingID"
              class="sr-only peer"
              :aria-label="
                t('option_label', {
                  name: course_setting.name,
                  price: formatPrice(course_setting.amount, course_setting.fee_currency),
                  discount:
                    course_setting.discount && course_setting.discount < 0
                      ? formatPrice(course_setting.discount, course_setting.fee_currency)
                      : '',
                })
              "
            />
            <span
              class="flex items-center cursor-pointer rounded-md text-primary px-2 py-3 sm:px-3 sm:py-3.5 transition-all duration-200 peer-focus-visible:ring-4 peer-focus-visible:ring-primary peer-focus-visible:ring-opacity-50 peer-focus-visible:outline-none"
              role="presentation"
            >
              <i
                class="block flex-none h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-white mr-2 sm:mr-1"
                aria-hidden="true"
              ></i>
              <span class="flex-1 min-w-0">
                <span class="block sm:inline font-bold text-custom-grey-1">
                  {{ course_setting.name }}
                </span>
                <span
                  v-if="
                    course_setting.commitment_period_enabled &&
                    course_setting.commitment_months
                  "
                  class="inline-block ml-0 sm:ml-2 mt-1 sm:mt-0 px-2 py-1 text-xs font-semibold text-blue-900 bg-blue-100 rounded"
                  :aria-label="
                    t('commitment_badge_full', {
                      months: course_setting.commitment_months,
                    })
                  "
                >
                  {{
                    t("commitment_badge", {
                      months: course_setting.commitment_months,
                    })
                  }}
                </span>
              </span>
              <span class="flex-none ml-3 text-right">
                <span
                  class="block font-bold text-custom-grey-1 whitespace-nowrap"
                >
                  {{ formatPrice(course_setting.amount, course_setting.fee_currency) }}
                </span>
                <span
                  v-if="course_setting.discount && course_setting.discount < 0"
                  class="block text-sm text-green-800 font-semibold whitespace-nowrap"
                  :aria-label="
                    t('discount_label', {
                      amount: formatPrice(
                        Math.abs(course_setting.discount),
                        course_setting.fee_currency
                      ),
                    })
                  "
                >
                  {{ formatPrice(course_setting.discount, course_setting.fee_currency) }}
                </span>
              </span>
            </span>
          </label>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ICourseSetting } from "@/scripts/application/types";
import { useI18n } from "vue-i18n";
import { useCurrency } from "@/scripts/application/composables/useCurrency";
import { computed } from "vue";
const props = defineProps<{
  course_settings: Array<ICourseSetting>;
  base_price: number | null;
  modelValue: number | null;
}>();
const emit = defineEmits<{
  (event: "update:modelValue", value: number | null): void;
}>();

const { formatPrice } = useCurrency();

const selectedCourseSettingID = computed<number | null>({
  get() {
    return props.modelValue || null;
  },
  set(value) {
    emit("update:modelValue", value ?? null);
  },
});

const courseSettingsWithDiscount = computed<ICourseSetting[]>(() => {
  if (!props.base_price) return props.course_settings;

  const basePrice = props.base_price;
  return props.course_settings.map((course_setting) => {
    return {
      ...course_setting,
      discount: course_setting.amount - basePrice * course_setting.duration,
    };
  });
});
const { t } = useI18n({ inheritLocale: true, useScope: "local" });
</script>

<i18n lang="yaml">
en:
  commitment_badge: "{months} mo commitment"
  commitment_badge_full: "{months} month commitment period"
  option_label: "{name} - {price} {discount}"
  discount_label: "Save {amount}"
nb:
  commitment_badge: "{months} mnd bindingstid"
  commitment_badge_full: "{months} måneders bindingstid"
  option_label: "{name} - {price} {discount}"
  discount_label: "Spar {amount}"
uk:
  commitment_badge: "{months} міс зобов'язання"
  commitment_badge_full: "{months} місяць період зобов'язання"
  option_label: "{name} - {price} {discount}"
  discount_label: "Заощадьте {amount}"
es:
  commitment_badge: "{months} mes compromiso"
  commitment_badge_full: "{months} meses de período de compromiso"
  option_label: "{name} - {price} {discount}"
  discount_label: "Ahorra {amount}"
pl:
  commitment_badge: "{months} mies zobowiązanie"
  commitment_badge_full: "{months} miesięcy okres zobowiązania"
  option_label: "{name} - {price} {discount}"
  discount_label: "Oszczędź {amount}"
</i18n>

<style scoped>
/* Ensure focus is visible for keyboard navigation */
.ln-radiobutton input[type="radio"]:focus-visible + span {
  @apply ring-4 ring-primary ring-opacity-50 outline-none;
}

/* Add hover state for better interactivity */
.ln-radiobutton label:hover span {
  @apply bg-gray-50;
}

/* Ensure active/selected state is clear */
.ln-radiobutton input[type="radio"]:checked + span {
  @apply bg-primary-50 border-2 border-primary;
}

/* Make the custom radio indicator more prominent when focused */
.ln-radiobutton input[type="radio"]:focus-visible + span > i {
  @apply ring-2 ring-primary ring-offset-2;
}

/* Checked radio indicator */
.ln-radiobutton input[type="radio"]:checked + span > i {
  @apply bg-primary border-4 border-white shadow-md;
}
</style>
